import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle2,
  XCircle,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  TrendingUp,
  Search,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";
import seoIconAsset from "@/assets/seo.png.asset.json";
const seoIcon = seoIconAsset.url;

/* ───── Scroll-in animation ───── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function AnimateIn({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ───── DATA ───── */

const setupItems = [
  "Auditoría SEO completa (técnico + on-page + contenido)",
  "Configuración SEO on-page de tu sitio",
  "Configuración de sitemap y schema markup",
  "Setup de Google Search Console y Google Analytics 4",
  "Keyword research inicial (definición de palabras clave objetivo)",
];

const monthlyItems = [
  { bold: "4 artículos de blog al mes", rest: ", optimizados con keywords primarias y secundarias" },
  { bold: "Optimización con puntuación máxima", rest: " en RankMath" },
  { bold: "Optimización on-page continua", rest: " de tu sitio" },
  { bold: "Reporte mensual", rest: " de posicionamiento y resultados" },
];

const notIncluded = [
  "Linkbuilding masivo (servicio aparte)",
  "Migraciones SEO de webs grandes",
  "Contenido en idiomas distintos al español",
  "SEO Local / posicionamiento en Google Maps (es el servicio de SEO Local)",
  "Campañas de pago en Google (es el servicio de Google Ads)",
];

const processSteps = [
  { n: "01", title: "Contratas el servicio", desc: "Eliges SEO en tu plan." },
  { n: "02", title: "Nos das acceso", desc: "A tu web, hosting, Search Console y Analytics." },
  { n: "03", title: "Setup en 14 días", desc: "Auditoría, base técnica y keyword research." },
  { n: "04", title: "Producción continua", desc: "4 artículos al mes y optimización on-page." },
  { n: "05", title: "Reporte mensual", desc: "Recibes el informe el día de tu ciclo de facturación." },
];


const results = [
  { metric: "+185%", desc: "de aumento en tráfico orgánico" },
  { metric: "120+", desc: "keywords en primera página de Google" },
  { metric: "+210%", desc: "más de visitas desde búsquedas" },
  { metric: "45", desc: "artículos posicionados en el top 10" },
];

/* ───── COMPONENT ───── */
const SEODemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      {/* ━━━ 1. HERO ━━━ */}
      <ServiceHero
        eyebrow="Posicionamiento orgánico"
        title={
          <>
            Posicionamiento <Highlight>SEO</Highlight>
          </>
        }
        description={
          <>
            Aparece en Google de forma orgánica y atrae clientes sin pagar por
            cada clic. Contenido optimizado, base técnica sólida y reportes
            claros{" "}
            <strong className="text-foreground font-semibold">cada mes.</strong>
          </>
        }
        primaryCta={{ label: "Contratar SEO", href: "/#pricing" }}
        secondaryCta={{ label: "Agendar reunión", href: "/#contact" }}
        decoration={
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl bg-card border border-border shadow-md flex items-center justify-center"
              style={{ animation: "float 6s ease-in-out infinite" }}
            >
              <img src={seoIcon} alt="SEO" className="w-9 h-9 object-contain" />
            </div>
            <div className="text-sm text-muted-foreground font-body">
              Google Search · Contenido · On-page
            </div>
          </div>
        }
        includes={["Auditoría", "Keywords", "Contenido", "On-page", "RankMath", "Reporte"]}
      />

      <IncludesExcludes
        includes={[
          "<strong>8 artículos de blog al mes</strong> (800-1.200 palabras cada uno)",
          "Imagen por artículo siguiendo la estética de tu web, con tu logo",
          "Temas elegidos por nosotros según keyword research",
          "Keywords primarias y secundarias por artículo",
          "Puntuación máxima en RankMath",
          "Optimización de las páginas existentes de tu web",
          "Setup inicial: auditoría completa, on-page, sitemap, schema, Search Console + GA4",
          "Informe mensual el día de tu ciclo de facturación",
        ]}
        excludes={[
          "Aprobación previa de artículos (publicación directa, sin tandas de revisión)",
          "Linkbuilding masivo",
          "Migraciones SEO de webs grandes",
          "Contenido en idiomas distintos al español",
          "SEO Local (es otro servicio)",
        ]}
      />

      {/* ━━━ 3 + 4. SETUP & MENSUAL ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Setup */}
          <AnimateIn>
            <div className="h-full rounded-3xl border border-border bg-card p-8 md:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-5">
                <CalendarClock size={14} />
                Primeros 14 días
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-3">
                Puesta en marcha
              </h3>
              <p className="text-muted-foreground mb-6">
                Al contratar el servicio, durante los primeros 14 días dejamos
                toda la base técnica de tu SEO lista para empezar a posicionar.
              </p>
              <ul className="space-y-3">
                {setupItems.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm">
                    <CheckCircle2
                      size={18}
                      className="text-primary shrink-0 mt-0.5"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimateIn>

          {/* Mensual */}
          <AnimateIn delay={0.08}>
            <div className="h-full rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-8 md:p-10 shadow-lg shadow-primary/5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium mb-5">
                <Sparkles size={14} />
                Cada mes
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-3">
                Tu servicio mes a mes
              </h3>
              <p className="text-muted-foreground mb-6">
                Cada mes producimos contenido optimizado y trabajamos la base
                de tu sitio para mejorar tu posicionamiento. Esto es exactamente
                lo que incluye tu suscripción:
              </p>
              <ul className="space-y-3">
                {monthlyItems.map((item) => (
                  <li
                    key={item.bold}
                    className="flex items-start gap-3 text-sm"
                  >
                    <CheckCircle2
                      size={18}
                      className="text-primary shrink-0 mt-0.5"
                    />
                    <span>
                      <strong className="font-semibold">{item.bold}</strong>
                      {item.rest}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* ━━━ 5. NUESTRO ENFOQUE (DESTACADO) ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="relative rounded-3xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 md:p-12 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold mb-5">
                <ShieldCheck size={14} />
                Gestión de expectativas
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-4">
                El SEO es una carrera de fondo
              </h3>
              <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
                <p>
                  El posicionamiento orgánico{" "}
                  <strong className="text-foreground">
                    no da resultados de un día para otro
                  </strong>
                  . Es un trabajo continuo y acumulativo: cada artículo, cada
                  optimización y cada mejora técnica suma para que Google
                  confíe más en tu web.
                </p>
                <p>
                  Los primeros resultados suelen verse a partir de los{" "}
                  <strong className="text-foreground">3-6 meses</strong>, y
                  crecen con el tiempo.{" "}
                  <strong className="text-foreground">
                    Trabajamos con constancia y criterio para construir un
                    posicionamiento sólido y duradero, no atajos que Google
                    penaliza.
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ 6. QUÉ NO INCLUYE ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">
              Transparencia total
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Qué no incluye este servicio
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Para que todo quede claro desde el principio, estos puntos quedan
              fuera del servicio.
            </p>
          </div>
        </AnimateIn>

        <div className="max-w-3xl mx-auto space-y-3">
          {notIncluded.map((item, i) => (
            <AnimateIn key={item} delay={i * 0.05}>
              <div className="flex items-start gap-3 p-5 rounded-2xl border border-border bg-card">
                <XCircle
                  size={20}
                  className="text-destructive shrink-0 mt-0.5"
                />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ 7. CÓMO TRABAJAMOS ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              Nuestro proceso
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Cómo trabajamos
            </h2>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-5 gap-4 relative">
          {processSteps.map((step, i) => (
            <AnimateIn key={step.n} delay={i * 0.06}>
              <div className="h-full rounded-2xl bg-card border border-border p-6 hover:border-primary/30 hover:shadow-md transition-all relative">
                <div className="font-display text-4xl text-primary/30 mb-3">
                  {step.n}
                </div>
                <h4 className="font-semibold mb-2 text-sm">{step.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>


      {/* ━━━ 9. RESULTADOS ━━━ */}
      <section className="bg-muted/30 py-20 -mx-4 md:-mx-8 px-4 md:px-8 rounded-[2.5rem]">
        <div className="max-w-6xl mx-auto">
          <AnimateIn>
            <div className="text-center mb-12">
              <Badge variant="secondary" className="mb-4">
                <TrendingUp size={12} className="mr-1" />
                Resultados reales
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                Resultados que conseguimos
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Una muestra del tipo de resultados que generamos posicionando
                webs de nuestros clientes.
              </p>
            </div>
          </AnimateIn>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {results.map((r, i) => (
              <AnimateIn key={r.desc} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-card border border-border p-8 text-center hover:border-primary/30 hover:shadow-lg transition-all">
                  <div className="font-display text-4xl md:text-5xl text-primary font-bold mb-3">
                    {r.metric}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {r.desc}
                  </p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ 10. CTA FINAL ━━━ */}
      <section className="max-w-4xl mx-auto px-4">
        <AnimateIn>
          <div className="relative rounded-3xl bg-gradient-to-br from-primary via-primary/90 to-primary/70 p-10 md:p-16 text-center text-primary-foreground overflow-hidden shadow-2xl shadow-primary/20">
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute -top-20 -left-20 w-64 h-64 bg-white/30 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl uppercase mb-4 leading-tight">
                ¿Listo para aparecer en Google?
              </h2>
              <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                Elige tu plan y empezamos a construir tu posicionamiento en los
                próximos 14 días.
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-background text-foreground font-semibold hover:scale-105 transition-transform shadow-lg"
              >
                Contratar ahora
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default SEODemo;
